import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { submitSupportRequest } from '../api/support';
import {
  clearSupportReportDraft,
  readSupportReportDraft,
} from '../utils/supportReports';

const MAX_SCREENSHOT_BYTES = 650 * 1024;
const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const SUPPORT_EMAIL = 'support@thetavern.fr';
const SUPPORT_CATEGORIES = ['bug', 'feedback', 'support'];

const normalizeSupportCategory = (value) =>
  SUPPORT_CATEGORIES.includes(String(value || '').toLowerCase())
    ? String(value).toLowerCase()
    : '';

const fileToAttachment = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || '');
      const content = result.includes(',') ? result.split(',').pop() : '';
      resolve({
        filename: file.name,
        contentType: file.type,
        size: file.size,
        content,
      });
    };
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });

const pageLabel = (pageName, page) => pageName || page || 'The Tavern';

const prefilledSubject = (draft, t) =>
  draft ? t('support.form.prefillSubject', { page: pageLabel(draft.pageName, draft.page) }) : '';

const prefilledDescription = (draft, t) => {
  if (!draft) return '';

  const technicalDetails = [
    ['Page', draft.page],
    ['Previous route', draft.previousRoute],
    ['Error type', draft.errorType],
    ['Endpoint', [draft.method, draft.endpoint].filter(Boolean).join(' ')],
    ['HTTP status', draft.status],
    ['Request ID', draft.requestId],
    ['Error message', draft.errorMessage],
    ['Timestamp', draft.timestamp],
    ['Browser', typeof navigator !== 'undefined' ? navigator.userAgent : ''],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => `- ${label}: ${value}`)
    .join('\n');

  return [
    t('support.form.whatDoing'),
    '',
    '',
    t('support.form.technicalDetails'),
    technicalDetails,
    draft.stack ? `\n${t('support.form.stackTrace')}\n${draft.stack}` : '',
  ]
    .filter(Boolean)
    .join('\n');
};

const supportCategoryLabel = (category, t) =>
  t(`support.categories.${category}`, { defaultValue: category || 'bug' });

const manualReportText = ({
  category,
  description,
  errorDraft,
  screenshot,
  subject,
  t,
}) => {
  const metadata = [
    ['Category', supportCategoryLabel(category, t)],
    ['Subject', subject],
    ['Current URL', typeof window !== 'undefined' ? window.location.href : ''],
    ['Previous route', errorDraft?.previousRoute],
    ['Endpoint', errorDraft?.endpoint ? [errorDraft.method, errorDraft.endpoint].filter(Boolean).join(' ') : ''],
    ['HTTP status', errorDraft?.status],
    ['Request ID', errorDraft?.requestId],
    ['Error message', errorDraft?.errorMessage],
    ['Browser', typeof navigator !== 'undefined' ? navigator.userAgent : ''],
    ['Platform', typeof navigator !== 'undefined' ? navigator.platform : ''],
    ['Timestamp', new Date().toISOString()],
    ['App version', process.env.REACT_APP_VERSION || process.env.REACT_APP_BUILD || 'web'],
    ['Screenshot', screenshot?.name || 'none'],
    ['Stack trace', errorDraft?.stack],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}: ${value}`)
    .join('\n');

  return [description, '', '---', metadata].filter(Boolean).join('\n');
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const emptySupportForm = () => ({
  category: '',
  subject: '',
  description: '',
  screenshot: null,
});

const SupportPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('report');
  const requestedCategory = normalizeSupportCategory(
    searchParams.get('category') || searchParams.get('type')
  );
  const [errorDraft, setErrorDraft] = useState(null);
  const [category, setCategory] = useState(requestedCategory);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotError, setScreenshotError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const clearedReportRef = useRef(null);

  useEffect(() => {
    if (reportId && clearedReportRef.current === reportId) {
      setErrorDraft(null);
      setCategory(requestedCategory);
      setSubject('');
      setDescription('');
      return;
    }

    const draft = readSupportReportDraft(reportId);
    if (!draft) {
      setCategory(requestedCategory);
      return;
    }

    setErrorDraft(draft);
    setCategory(normalizeSupportCategory(draft.category) || 'bug');
    setSubject(prefilledSubject(draft, t));
    setDescription(prefilledDescription(draft, t));
  }, [reportId, requestedCategory, t]);

  const categoryOptions = useMemo(
    () => [
      {
        category: 'bug',
        icon: BugReportOutlinedIcon,
        title: t('support.actions.bug.title'),
        copy: t('support.actions.bug.copy'),
        cta: t('support.actions.bug.cta'),
      },
      {
        category: 'feedback',
        icon: FeedbackOutlinedIcon,
        title: t('support.actions.feedback.title'),
        copy: t('support.actions.feedback.copy'),
        cta: t('support.actions.feedback.cta'),
      },
      {
        category: 'support',
        icon: SupportAgentOutlinedIcon,
        title: t('support.actions.support.title'),
        copy: t('support.actions.support.copy'),
        cta: t('support.actions.support.cta'),
      },
    ],
    [t]
  );

  const handleScreenshotChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    setScreenshotError('');

    if (!file) return;

    if (!SCREENSHOT_TYPES.includes(file.type)) {
      setScreenshot(null);
      setScreenshotError(t('support.form.screenshotInvalid'));
      return;
    }

    if (file.size > MAX_SCREENSHOT_BYTES) {
      setScreenshot(null);
      setScreenshotError(t('support.form.screenshotTooLarge'));
      return;
    }

    setScreenshot(file);
  };

  const handleCopyReport = async () => {
    const report = manualReportText({
      category,
      description: description.trim(),
      errorDraft,
      screenshot,
      subject: subject.trim(),
      t,
    });

    try {
      await copyText(report);
      setCopyFeedback(t('support.feedback.copied'));
    } catch {
      setCopyFeedback(t('support.feedback.copyError'));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    setFormError('');
    setFormSuccess('');
    setCopyFeedback('');
    setScreenshotError('');

    const trimmedSubject = subject.trim();
    const trimmedDescription = description.trim();

    if (!category || !trimmedSubject || !trimmedDescription) {
      setFormError(t('support.form.required'));
      return;
    }

    setSubmitting(true);

    try {
      const attachment = screenshot ? await fileToAttachment(screenshot) : null;

      await submitSupportRequest({
        category,
        subject: trimmedSubject,
        description: trimmedDescription,
        screenshot: attachment,
        metadata: {
          currentUrl: window.location.href,
          previousRoute: errorDraft?.previousRoute,
          endpoint: errorDraft?.endpoint
            ? [errorDraft.method, errorDraft.endpoint].filter(Boolean).join(' ')
            : undefined,
          status: errorDraft?.status,
          requestId: errorDraft?.requestId,
          errorMessage: errorDraft?.errorMessage,
          stack: errorDraft?.stack,
          browser: navigator.userAgent,
          platform: navigator.platform,
          timestamp: new Date().toISOString(),
          appVersion: process.env.REACT_APP_VERSION || process.env.REACT_APP_BUILD || 'web',
        },
      });

      const successMessage = t('support.feedback.success');
      const resetForm = emptySupportForm();
      setFormSuccess(successMessage);
      setCategory(resetForm.category);
      setSubject(resetForm.subject);
      setDescription(resetForm.description);
      setScreenshot(resetForm.screenshot);
      setScreenshotError('');
      setCopyFeedback('');
      setErrorDraft(null);
      clearedReportRef.current = reportId;
      clearSupportReportDraft(reportId);
    } catch (error) {
      const message = t('support.feedback.errorWithFallback', { email: SUPPORT_EMAIL });
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategory = categoryOptions.find((option) => option.category === category);

  return (
    <Stack spacing={4} sx={{ py: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.95fr) minmax(300px, 0.55fr)' },
          gap: { xs: 3, md: 4 },
          alignItems: 'end',
        }}
      >
        <Box>
          <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
            {t('support.eyebrow')}
          </Typography>
          <Typography component="h1" variant="h1">
            {t('support.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 760 }}>
            {t('support.subtitle')}
          </Typography>
        </Box>

        <TextField
          fullWidth
          aria-label={t('support.search.label')}
          placeholder={t('support.search.placeholder')}
          helperText={t('support.search.helper')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack
            component="form"
            spacing={2.5}
            onSubmit={handleSubmit}
            noValidate
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#D7A04B', 0.26),
                  color: 'primary.main',
                }}
              >
                <HelpOutlineOutlinedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" color="secondary.dark">
                  {selectedCategory?.title || t('support.form.categoryPrompt')}
                </Typography>
                <Typography variant="h2" sx={{ fontSize: { xs: 24, md: 30 } }}>
                  {t('support.form.title')}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' },
                gap: 2,
              }}
            >
              <FormControl size="small" fullWidth required>
                <InputLabel id="support-category-label" shrink>
                  {t('support.form.category')}
                </InputLabel>
                <Select
                  labelId="support-category-label"
                  label={t('support.form.category')}
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    const option = categoryOptions.find((item) => item.category === selected);

                    return option ? (
                      option.title
                    ) : (
                      <Typography component="span" color="text.secondary">
                        {t('support.form.categoryPlaceholder')}
                      </Typography>
                    );
                  }}
                >
                  <MenuItem value="" disabled>
                    {t('support.form.categoryPlaceholder')}
                  </MenuItem>
                  {categoryOptions.map(({ category: optionCategory, icon: Icon, title, copy }) => (
                    <MenuItem key={optionCategory} value={optionCategory}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            color: 'primary.main',
                            bgcolor: alpha('#0F4C45', 0.1),
                            flex: '0 0 auto',
                          }}
                        >
                          <Icon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={900}>{title}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {copy}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={t('support.form.subject')}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                inputProps={{ maxLength: 140 }}
                required
                fullWidth
              />
            </Box>

            <TextField
              label={t('support.form.description')}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              multiline
              minRows={6}
              inputProps={{ maxLength: 5000 }}
              required
              fullWidth
            />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<AttachFileOutlinedIcon />}
              >
                {screenshot ? t('support.form.replaceScreenshot') : t('support.form.addScreenshot')}
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleScreenshotChange}
                />
              </Button>
              {screenshot ? (
                <Chip
                  label={screenshot.name}
                  onDelete={() => setScreenshot(null)}
                  sx={{ maxWidth: { xs: '100%', sm: 320 } }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('support.form.screenshotHelper')}
                </Typography>
              )}
            </Stack>

            {screenshotError ? <Alert severity="error">{screenshotError}</Alert> : null}
            {formSuccess ? <Alert severity="success">{formSuccess}</Alert> : null}
            {formError ? (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<ContentCopyOutlinedIcon />}
                    onClick={handleCopyReport}
                  >
                    {t('support.form.copyReport')}
                  </Button>
                }
              >
                <Stack spacing={0.75}>
                  <Typography variant="body2">{formError}</Typography>
                  <Typography variant="body2" fontWeight={900}>{SUPPORT_EMAIL}</Typography>
                  {copyFeedback ? <Typography variant="caption">{copyFeedback}</Typography> : null}
                </Stack>
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !category}
                startIcon={submitting ? <CircularProgress color="inherit" size={18} /> : null}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                {submitting ? t('support.form.sending') : t('support.form.submit')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('support.form.metadata')}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default SupportPage;
